import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';

const emptyForm = { name: '', email: '', password: '', roleId: '' };
const PAGE_SIZE = 10;

export default function TechUsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        api.get('/admin/tech-users'),
        api.get('/admin/roles'),
      ]);
      setUsers(usersRes.data.users);
      setRoles(rolesRes.data.roles);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter(u =>
      (!q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)) &&
      (!roleFilter || String(u.roleId) === roleFilter)
    );
  }, [users, search, roleFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearchChange = (e) => { setSearch(e.target.value); setPage(1); };
  const handleRoleFilterChange = (e) => { setRoleFilter(e.target.value); setPage(1); };

  const openCreate = () => { setForm(emptyForm); setEditId(null); setError(''); setShowModal(true); };

  const openEdit = (user) => {
    setForm({ name: user.name, email: user.email, password: '', roleId: user.roleId });
    setEditId(user.id);
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      if (editId && !payload.password) delete payload.password;
      if (editId) {
        await api.put(`/admin/tech-users/${editId}`, payload);
      } else {
        await api.post('/admin/tech-users', payload);
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await api.delete(`/admin/tech-users/${id}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete user');
    }
  };

  return (
    <div>
      <div className="table-card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <span className="card-title">👥 Tech Users</span>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              style={{ padding: '8px 12px', background: '#1e2340', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, fontSize: 13, outline: 'none', color: 'var(--text-primary)', width: 220 }}
              placeholder="Search by name or email…"
              value={search}
              onChange={handleSearchChange}
            />
            <select
              style={{ padding: '8px 12px', background: '#1e2340', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, fontSize: 13, outline: 'none', color: 'var(--text-primary)', cursor: 'pointer' }}
              value={roleFilter}
              onChange={handleRoleFilterChange}
            >
              <option value="">All Roles</option>
              {roles.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
            </select>
            <button className="action-btn" onClick={openCreate}>+ Add Tech User</button>
          </div>
        </div>

      {loading ? <p>Loading...</p> : (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888' }}>No tech users found</td></tr>
                ) : paginated.map((user, i) => (
                  <tr key={user.id}>
                    <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>{user.role?.name || '-'}</td>
                    <td>
                      <button className="edit-btn" onClick={() => openEdit(user)}>Edit</button>
                      <button className="del-btn" onClick={() => handleDelete(user.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Showing {filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button style={{ minWidth: 32, height: 32, padding: '0 10px', border: '1px solid rgba(255,255,255,0.15)', background: '#1e2340', borderRadius: 5, cursor: 'pointer', fontSize: 13, color: '#cbd5e1' }} onClick={() => setPage(p => p - 1)} disabled={page === 1}>Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  style={{
                    minWidth: 32, height: 32, padding: '0 10px', borderRadius: 5,
                    border: p === page ? '1px solid var(--accent)' : '1px solid rgba(255,255,255,0.15)',
                    cursor: 'pointer', fontSize: 13,
                    background: p === page ? 'var(--accent)' : '#1e2340',
                    color: p === page ? '#fff' : '#cbd5e1',
                    fontWeight: p === page ? 600 : 400,
                  }}
                >{p}</button>
              ))}
              <button style={{ minWidth: 32, height: 32, padding: '0 10px', border: '1px solid rgba(255,255,255,0.15)', background: '#1e2340', borderRadius: 5, cursor: 'pointer', fontSize: 13, color: '#cbd5e1' }} onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>Next</button>
            </div>
          </div>
        </>
      )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{editId ? 'Edit Tech User' : 'Add Tech User'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>{editId ? 'New Password (leave blank to keep)' : 'Password'}</label>
                <input className="form-input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required={!editId} />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select className="form-input" value={form.roleId} onChange={e => setForm(f => ({ ...f, roleId: e.target.value }))} required>
                  <option value="">Select a role</option>
                  {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              {error && <p style={{ color: '#ef4444', fontSize: 13 }}>{error}</p>}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="action-btn" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
